import { useEffect, useState } from 'react'
import axiosReadInstance from '../../request/axiosReadInstance'
import { usePageLoading } from '../../context/PageLoadingContext'
import { isContentRecord } from '../../utils/cmsDetail'

export type ContentState = 'loading' | 'empty' | 'error' | 'success'
export const DETAIL_REQUEST_TIMEOUT_MS = 10_000

type DetailContent<T> =
    | { state: 'success'; data: T }
    | { state: Exclude<ContentState, 'success'>; data: null }

// Scoped to detail pages. Every request owns its result and loading counter.
export const useDetailContent = <T>(
    url: string | null,
    parse: (body: unknown) => T | null
): DetailContent<T> => {
    const [result, setResult] = useState<
        DetailContent<T> & { url: string | null }
    >({
        url,
        state: url ? 'loading' : 'empty',
        data: null,
    })
    const { incrementLoadingRequestCount, decrementLoadingRequestCount } =
        usePageLoading()

    useEffect(() => {
        if (!url) {
            setResult({ url, state: 'empty', data: null })
            return
        }

        let active = true
        let finished = false
        let timeout: ReturnType<typeof setTimeout> | undefined
        const controller = new AbortController()
        const finish = () => {
            if (finished) return
            finished = true
            if (timeout !== undefined) clearTimeout(timeout)
            decrementLoadingRequestCount()
        }

        setResult({ url, state: 'loading', data: null })
        incrementLoadingRequestCount()
        timeout = setTimeout(() => {
            if (!active || finished) return
            setResult({ url, state: 'error', data: null })
            finish()
            controller.abort()
        }, DETAIL_REQUEST_TIMEOUT_MS)

        const load = async () => {
            try {
                const response = await axiosReadInstance.get<unknown>(url, {
                    signal: controller.signal,
                    timeout: DETAIL_REQUEST_TIMEOUT_MS,
                })
                if (!active || finished) return
                const data = parse(response?.data)
                setResult(
                    data === null
                        ? { url, state: 'empty', data: null }
                        : { url, state: 'success', data }
                )
            } catch (error) {
                if (!active || finished) return
                const notFound =
                    isContentRecord(error) &&
                    isContentRecord(error.response) &&
                    error.response.status === 404
                setResult({
                    url,
                    state: notFound ? 'empty' : 'error',
                    data: null,
                })
            } finally {
                finish()
            }
        }

        void load()
        return () => {
            active = false
            controller.abort()
            finish()
        }
    }, [url, parse, incrementLoadingRequestCount, decrementLoadingRequestCount])

    // Do not flash the previous detail before the navigation effect starts.
    return result.url === url
        ? result
        : { state: url ? 'loading' : 'empty', data: null }
}
